import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import api from '@/lib/api';

export default function AdminPlans() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', slug: '', max_customers_per_gym: '', max_gyms: '', max_products: '', price: '', is_lifetime_free: false, trial_duration_days: '', level: '1', active: true });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchPlans = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/plans');
            setPlans(res.data.data);
        } catch {} finally { setLoading(false); }
    };

    useEffect(() => { fetchPlans(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ name: '', slug: '', max_customers_per_gym: '', max_gyms: '', max_products: '', price: '', is_lifetime_free: false, trial_duration_days: '', level: '1', active: true });
        setError('');
        setDialogOpen(true);
    };

    const openEdit = (plan) => {
        setEditing(plan);
        setForm({
            name: plan.name || '',
            slug: plan.slug || '',
            max_customers_per_gym: plan.max_customers_per_gym || '',
            max_gyms: plan.max_gyms || '',
            max_products: plan.features?.max_products || '',
            price: plan.price || '',
            is_lifetime_free: plan.is_lifetime_free || false,
            trial_duration_days: plan.trial_duration_days || '',
            level: String(plan.level ?? '1'),
            active: plan.active !== false,
        });
        setError('');
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const data = {
                ...form,
                max_customers_per_gym: parseInt(form.max_customers_per_gym),
                max_gyms: parseInt(form.max_gyms),
                price: parseFloat(form.price),
                level: parseInt(form.level),
                trial_duration_days: form.trial_duration_days ? parseInt(form.trial_duration_days) : null,
                features: form.max_products ? { max_products: parseInt(form.max_products) } : null,
            };
            if (editing) {
                await api.put(`/admin/plans/${editing.id}`, data);
            } else {
                await api.post('/admin/plans', data);
            }
            setDialogOpen(false);
            fetchPlans();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar este plan?')) return;
        try {
            await api.delete(`/admin/plans/${id}`);
            fetchPlans();
        } catch {}
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide">Planes</h1>
                <Button onClick={openCreate} className="w-full sm:w-auto"><Plus className="h-4 w-4 mr-2" />Nuevo Plan</Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {loading ? (
                    <p className="text-white/60 col-span-full text-center py-8">Cargando...</p>
                ) : plans.length === 0 ? (
                    <p className="text-white/60 col-span-full text-center py-8">No hay planes creados.</p>
                ) : (
                    plans.map(plan => (
                        <Card key={plan.id} className="relative bg-[#0a0a0a] border-hairline-dark">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold">{plan.name}</h3>
                                    <div className="flex gap-1">
                                        {plan.is_lifetime_free && <Badge variant="success">Gratis</Badge>}
                                        {plan.trial_duration_days && <Badge variant="outline">Prueba {plan.trial_duration_days}d</Badge>}
                                        <Badge variant="secondary">Nv. {plan.level}</Badge>
                                        <Badge variant={plan.active ? 'success' : 'secondary'}>{plan.active ? 'Activo' : 'Inactivo'}</Badge>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2 mb-4">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-white/60">Slug:</span>
                                        <span className="font-mono">{plan.slug}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-white/60">Max. gimnasios:</span>
                                        <span className="font-semibold">{plan.max_gyms}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-white/60">Max. alumnos/gym:</span>
                                        <span className="font-semibold">{plan.max_customers_per_gym}</span>
                                    </div>
                                    {plan.features?.max_products && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-white/60">Max. productos tienda:</span>
                                            <span className="font-semibold">{plan.features.max_products}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-sm">
                                        <span className="text-white/60">Precio:</span>
                                        <span className="font-semibold">${plan.price}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-white/60">Nivel:</span>
                                        <span className="font-semibold">{plan.level}</span>
                                    </div>
                                    {plan.trial_duration_days && (
                                        <div className="flex justify-between text-sm">
                                            <span className="text-white/60">Duración prueba:</span>
                                            <span className="font-semibold">{plan.trial_duration_days} días</span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(plan)}>
                                        <Pencil className="h-3 w-3 mr-1" />Editar
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => handleDelete(plan.id)}>
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-hairline-dark">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Editar Plan' : 'Nuevo Plan'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        {error && <div className="bg-[#0a0a0a] border border-hairline-dark text-white text-sm p-3 rounded-md">{error}</div>}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Nombre</Label>
                                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                            </div>
                            <div className="space-y-2">
                                <Label>Slug</Label>
                                <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} required />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Max. Gimnasios</Label>
                                <Input type="number" min="1" value={form.max_gyms} onChange={e => setForm({ ...form, max_gyms: e.target.value })} required />
                            </div>
                            <div className="space-y-2">
                                <Label>Max. Alumnos por Gimnasio</Label>
                                <Input type="number" min="1" value={form.max_customers_per_gym} onChange={e => setForm({ ...form, max_customers_per_gym: e.target.value })} required />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Precio</Label>
                                <Input type="number" min="0" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required />
                            </div>
                            <div className="space-y-2">
                                <Label>Max. Productos Tienda</Label>
                                <Input type="number" min="1" value={form.max_products} onChange={e => setForm({ ...form, max_products: e.target.value })} placeholder="Dejar vacío para ilimitado" />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Duración de prueba (días)</Label>
                                <Input type="number" min="1" value={form.trial_duration_days} onChange={e => setForm({ ...form, trial_duration_days: e.target.value })} placeholder="Dejar vacío si no es plan de prueba" />
                            </div>
                            <div className="space-y-2">
                                <Label>Nivel</Label>
                                <Input type="number" min="1" value={form.level} onChange={e => setForm({ ...form, level: e.target.value })} required />
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.is_lifetime_free} onChange={e => setForm({ ...form, is_lifetime_free: e.target.checked })} />
                                De por vida gratuito
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} />
                                Activo
                            </label>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
